import React from "react";
import { Link } from "react-router-dom";
import { MEDIA_URL } from "../../utils";
import { getCountryName } from "../../utils/countryUtils";
import defaultImg from "../../img/LOGO_AROME.jpg";
import { usePermissions } from "../../hooks/usePermissions";

const SellPointCard = ({ item, onEdit, onDeactivate }) => {
  const { hasPermission } = usePermissions();

  // Déterminer si c'est un entrepôt ou un store
  const isWarehouse = !!item.is_entrepot;

  // Permissions selon le type
  const canEdit = isWarehouse
    ? hasPermission("canEditWarehouse")
    : hasPermission("canEditStore");

  const canDelete = isWarehouse
    ? hasPermission("canDeleteWarehouse")
    : hasPermission("canDeleteStore");

  return (
    <div className="group bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-xl hover:border-amber-200 transition-all duration-300 overflow-hidden">
      {/* Image Header */}
      <div className="relative h-48 bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200 overflow-hidden">
        <img
          src={item.photo ? `${MEDIA_URL}${item.photo}` : defaultImg}
          alt={item.nom}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
      </div>

      {/* Contenu principal */}
      <div className="p-5">
        {/* En-tête avec titre et actions */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1 min-w-0">
            <h3
              className="text-xl font-bold text-gray-900 truncate mb-1"
              title={item.nom}
            >
              {item.nom}
            </h3>

            {/* Métadonnées */}
          </div>

          {/* Actions */}
          <div className="flex gap-2 ml-4">
            <ActionButton
              as={Link}
              to={`/stock/sell-point-details/${item.id}`}
              color="blue"
              icon="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Zm-9 4a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z"
              label="Voir détails"
              size="small"
            />

            {canEdit && (
              <ActionButton
                onClick={onEdit}
                color="amber"
                icon="M14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
                label="Modifier"
                size="small"
              />
            )}

            {canDelete && (
              <ActionButton
                onClick={onDeactivate}
                color="red"
                icon="M6 18 18 6M6 6l12 12"
                label="Désactiver"
                size="small"
              />
            )}
          </div>
        </div>

        {/* Objectifs - uniquement pour les boutiques */}
        {!item.is_entrepot && (
          <div className="bg-gradient-to-r from-blue-50 to-amber-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <div className="text-2xl font-bold text-blue-600">
                  {item.objectif_journalier || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Objectif journalier
                </div>
              </div>

              <div className="w-px h-8 bg-gray-300 mx-4"></div>

              <div className="text-center flex-1">
                <div className="text-2xl font-bold text-amber-600">
                  {item.objectif_mensuel || 0}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Objectif mensuel
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <LocationIcon />
            <span className="truncate">
              {item.quartier || "Quartier non spécifié"}
            </span>
          </div>

          {item.pays && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CountryIcon />
              <span className="truncate">
                {getCountryName(item.pays) || item.pays}
              </span>
            </div>
          )}
        </div>

        {/* Type d'établissement - uniquement pour les entrepôts */}
        {item.is_entrepot && (
          <div className="bg-gradient-to-r from-gray-50 to-amber-50 rounded-lg p-4 mt-4">
            <div className="text-center">
              <span className="px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-full shadow-sm">
                Entrepot
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ActionButton = ({
  onClick,
  color,
  icon,
  label,
  size = "normal",
  as: Component = "button",
  ...props
}) => {
  const colorClasses = {
    red: "bg-red-500 hover:bg-red-600 text-white",
    amber: "bg-amber-500 hover:bg-amber-600 text-white",
    green: "bg-green-500 hover:bg-green-600 text-white",
    blue: "bg-blue-500 hover:bg-blue-600 text-white",
  };

  const sizeClasses = {
    small: "p-2",
    normal: "p-3",
  };

  const iconSizeClasses = {
    small: "w-4 h-4",
    normal: "w-5 h-5",
  };

  return (
    <Component
      onClick={onClick}
      className={`${sizeClasses[size]} rounded-lg ${colorClasses[color]} transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md`}
      aria-label={label}
      {...props}
    >
      <svg
        className={iconSizeClasses[size]}
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        fill="none"
        viewBox="0 0 24 24"
      >
        <path
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d={icon}
        />
      </svg>
    </Component>
  );
};

const LocationIcon = () => (
  <svg
    className="w-4 h-4 text-amber-500"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M17.8 13.938a8 8 0 1 0-11.6 0L12 21l5.8-7.062Z"
    />
  </svg>
);

const CountryIcon = () => (
  <svg
    className="w-4 h-4 text-blue-500"
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z"
    />
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15 9.354a4 4 0 1 1-6 0M15 9.354c0 .953-.35 1.826-.926 2.492M15 9.354c1.557-.89 2.526-2.615 2.526-4.528C17.526 2.615 15.04 1 12 1S6.474 2.615 6.474 4.826c0 1.913.97 3.638 2.526 4.528M9 9.354c.576.666.926 1.539.926 2.492"
    />
  </svg>
);

export default React.memo(SellPointCard);
